import React, { useMemo } from 'react';
import { Marker, Tooltip, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  NormalizedManufacturingUnit,
  DATA_SOURCE_COLORS,
  CONFIDENCE_LEVELS,
} from '../../types/gaushalaMap.types';

/**
 * Manufacturing Unit Marker Component
 *
 * Displays a marker for each manufacturing unit on the map with:
 * - Custom factory icon (different from gaushala cow icon)
 * - Tooltip showing unit name
 * - Clickable popup with details
 */

interface ManufacturingMarkerProps {
  unit: NormalizedManufacturingUnit;
  onClick?: (unit: NormalizedManufacturingUnit) => void;
  isSelected?: boolean;
}

/**
 * Create custom Leaflet icon for manufacturing units
 * Uses a factory/industrial icon to differentiate from gaushalas
 */
const createManufacturingIcon = (dataSource: string, isSelected: boolean = false) => {
  // Use blue color for manufacturing units
  const baseColor = '#3b82f6'; // Blue-500
  const size = isSelected ? 40 : 32;

  const html = `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${size}px;
      height: ${size}px;
      background-color: ${baseColor};
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3)${isSelected ? ', 0 0 12px ' + baseColor : ''};
      color: white;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      transition: transform 0.2s;
    ">
      🏭
    </div>
  `;

  return L.divIcon({
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
    className: 'manufacturing-marker',
  });
};

/**
 * Manufacturing Unit Popup Component
 */
const ManufacturingPopup: React.FC<{ unit: NormalizedManufacturingUnit }> = ({ unit }) => {
  const confidenceInfo = CONFIDENCE_LEVELS[unit.confidence];

  return (
    <div className="p-2 min-w-[220px]">
      {/* Header */}
      <div className="border-b border-gray-200 pb-2 mb-2">
        <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
          🏭 {unit.name}
        </h3>
        <span
          className="inline-block px-2 py-0.5 text-xs rounded-full mt-1"
          style={{
            backgroundColor: confidenceInfo.color + '20',
            color: confidenceInfo.color,
          }}
        >
          {confidenceInfo.emoji} {confidenceInfo.label}
        </span>
      </div>

      {/* Location Info */}
      <div className="space-y-2 text-sm">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase">Location</p>
          <p className="text-gray-700">{unit.address}</p>
          <p className="text-xs text-gray-500 mt-1">
            {unit.latitude.toFixed(4)}°N, {unit.longitude.toFixed(4)}°E
          </p>
        </div>

        {/* Unit Code */}
        {unit.unitCode && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Unit Code</p>
            <p className="text-gray-700">{unit.unitCode}</p>
          </div>
        )}

        {/* Metadata */}
        {unit.metadata.capacity && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Capacity</p>
            <p className="text-gray-700">{unit.metadata.capacity} units</p>
          </div>
        )}

        {unit.metadata.status && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Status</p>
            <span
              className={`inline-block px-2 py-0.5 text-xs rounded ${
                unit.metadata.status === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {unit.metadata.status}
            </span>
          </div>
        )}

        {/* Data Source */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase">Data Source</p>
          <p className="text-xs text-gray-600 flex items-center gap-1">
            {unit.dataSource === 'database' ? '✓' : '🌐'}
            {unit.dataSource === 'database' ? 'From Database' : 'Geocoded'}
          </p>
        </div>
      </div>
    </div>
  );
};

const ManufacturingMarker: React.FC<ManufacturingMarkerProps> = ({
  unit,
  onClick,
  isSelected = false,
}) => {
  const markerIcon = useMemo(
    () => createManufacturingIcon(unit.dataSource, isSelected),
    [unit.dataSource, isSelected]
  );

  const handleMarkerClick = () => {
    if (onClick) {
      onClick(unit);
    }
  };

  return (
    <Marker
      position={[unit.latitude, unit.longitude]}
      icon={markerIcon}
      eventHandlers={{
        click: handleMarkerClick,
      }}
    >
      {/* Hover Tooltip */}
      <Tooltip
        permanent={false}
        direction="top"
        offset={[0, -10]}
        className="manufacturing-tooltip"
      >
        <div className="tooltip-content">
          <p className="font-semibold text-gray-800">🏭 {unit.name}</p>
          <p className="text-xs text-gray-600 truncate max-w-xs">
            {unit.address}
          </p>
        </div>
      </Tooltip>

      {/* Click Popup */}
      <Popup maxWidth={350} minWidth={250} className="manufacturing-popup">
        <ManufacturingPopup unit={unit} />
      </Popup>
    </Marker>
  );
};

export default ManufacturingMarker;
